export type BoatTypeId = "dinghy" | "speedboat" | "raft";
export type RefugeePhase = "seated" | "held" | "flying" | "swimming" | "sinking" | "shore" | "gone";

export interface BoatSpec {
  id: BoatTypeId;
  name: string;
  minCapacity: number;
  maxCapacity: number;
  baseSpeed: readonly [number, number];
  length: number;
  unlockAfter: number;
  weight: number;
}

export interface RandomSource {
  next(): number;
}

export interface RefugeeSave {
  version: 1;
  best: number;
  totalDeported: number;
}

export const WIDTH = 1_280;
export const HEIGHT = 720;
export const SHORE_X = 468;
export const DUNE_X = 78;
export const WATER_Y = 566;
export const GRAVITY = 1_850;
export const MAX_THROW_SPEED = 1_650;
export const FIXED_STEP = 1 / 120;
export const MAX_STRIKES = 3;
export const SAFE_DISTANCE = 290;
export const MAX_BOATS = 6;
export const MAX_REFUGEES = 26;
export const BOAT_SPAWN_X = WIDTH + 150;
export const GRAB_RADIUS = 66;
export const BOAT_SINK_SECONDS = 1.4;
export const BOARDING_DECK_Y = WATER_Y - 44;

export const BOAT_SPECS: Record<BoatTypeId, BoatSpec> = {
  dinghy: {
    id: "dinghy",
    name: "Dinghy",
    minCapacity: 3,
    maxCapacity: 5,
    baseSpeed: [46, 64],
    length: 152,
    unlockAfter: 0,
    weight: 6,
  },
  speedboat: {
    id: "speedboat",
    name: "Speedboat",
    minCapacity: 1,
    maxCapacity: 2,
    baseSpeed: [146, 182],
    length: 122,
    unlockAfter: 22,
    weight: 2,
  },
  raft: {
    id: "raft",
    name: "Overload Raft",
    minCapacity: 6,
    maxCapacity: 8,
    baseSpeed: [32, 44],
    length: 188,
    unlockAfter: 48,
    weight: 3,
  },
};

export class SeededRandom {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0 || 0x6d2b79f5;
  }

  next(): number {
    let value = this.state;
    value ^= value << 13;
    value ^= value >>> 17;
    value ^= value << 5;
    this.state = value >>> 0;
    return this.state / 0x1_0000_0000;
  }
}

export function randomSeed(): number {
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return values[0] ?? 1;
}

export function spawnIntervalSeconds(elapsed: number): number {
  const progress = clampUnit(elapsed / 210);
  return 4.8 - progress * 3.1;
}

export function speedScale(elapsed: number): number {
  return 1 + clampUnit(elapsed / 280) * 0.9;
}

export function availableBoatTypes(elapsed: number): BoatSpec[] {
  return Object.values(BOAT_SPECS).filter((spec) => elapsed >= spec.unlockAfter);
}

export function chooseBoatType(random: RandomSource, elapsed: number): BoatSpec {
  const pool = availableBoatTypes(elapsed);
  const fallback = BOAT_SPECS.dinghy;
  if (pool.length === 0) return fallback;
  const total = pool.reduce((sum, spec) => sum + spec.weight, 0);
  let roll = random.next() * total;
  for (const spec of pool) {
    roll -= spec.weight;
    if (roll <= 0) return spec;
  }
  return pool[0] ?? fallback;
}

export function passengerCount(random: RandomSource, spec: BoatSpec): number {
  const span = spec.maxCapacity - spec.minCapacity + 1;
  return spec.minCapacity + Math.floor(random.next() * span);
}

export function boatSpeed(random: RandomSource, spec: BoatSpec, elapsed: number): number {
  const [minimum, maximum] = spec.baseSpeed;
  return (minimum + random.next() * (maximum - minimum)) * speedScale(elapsed);
}

export function canSpawnBoat(liveBoats: number, liveRefugees: number): boolean {
  return liveBoats >= 0 && liveRefugees >= 0 && liveBoats < MAX_BOATS && liveRefugees < MAX_REFUGEES;
}

export function pointsForSplash(splashX: number): number {
  const distance = splashX - SHORE_X;
  if (distance >= 660) return 3;
  if (distance >= SAFE_DISTANCE + 170) return 2;
  if (distance >= SAFE_DISTANCE) return 1;
  return 0;
}

export function comboMultiplier(combo: number): number {
  return Math.min(4, 1 + Math.floor(Math.max(0, combo) / 4));
}

export function addStrikes(current: number, landed: number): number {
  return Math.min(MAX_STRIKES, Math.max(0, current) + Math.max(0, landed));
}

export function isGameOver(strikes: number): boolean {
  return strikes >= MAX_STRIKES;
}

export function isActiveCrewPhase(phase: RefugeePhase): boolean {
  return phase === "seated" || phase === "held" || phase === "flying";
}

export function landsOnBoatDeck(
  x: number,
  y: number,
  velocityY: number,
  boatX: number,
  boatLength: number,
): boolean {
  return velocityY > 0 && y >= BOARDING_DECK_Y && Math.abs(x - boatX) <= boatLength / 2 - 14;
}

export function canGrab(refugee: { touched: boolean; phase: RefugeePhase }): boolean {
  return !refugee.touched && (refugee.phase === "seated" || refugee.phase === "shore");
}

const DEFAULT_SAVE: RefugeeSave = { version: 1, best: 0, totalDeported: 0 };

export function parseRefugeeSave(value: string | null, legacyBest = 0): RefugeeSave {
  if (!value) return { ...DEFAULT_SAVE, best: legacyBest };
  try {
    const parsed = JSON.parse(value) as Partial<RefugeeSave>;
    return {
      version: 1,
      best: Math.max(legacyBest, finiteNonNegative(parsed.best)),
      totalDeported: finiteNonNegative(parsed.totalDeported),
    };
  } catch {
    return { ...DEFAULT_SAVE, best: legacyBest };
  }
}

function finiteNonNegative(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? Math.floor(value) : 0;
}

function clampUnit(value: number): number {
  return Math.min(1, Math.max(0, value));
}
